import { isEqual } from 'lodash';
import {
  alwaysEnabled,
  asList,
  asLines,
  asText,
  hasCostingSupplier,
  keepText,
  labelOf,
  labelsOf,
  LINE_SEPARATOR,
  resolveOptions,
  toNumericText,
  valueOf,
  valuesOf,
} from './cellFill';
import type {
  CoreKey,
  CoreMultiKey,
  CoreProductLine,
  FillLineOptions,
  FillSpec,
  FillStackConfig,
  GradingGroupLine,
  GradingGroupOption,
  GradingKey,
  LineValue,
  MultiSubmitGridContext,
  MultiSubmitRow,
  OptionSource,
  SelectOption,
} from './types';

/**
 * The options for one line, when the column has any.
 * @param optionsOf - the column's per-line resolver.
 * @param line - the line, absent past the end of the row's stack.
 * @param row - the row.
 * @param ctx - grid context.
 * @returns the options, or undefined for a free-text column or a missing line.
 */
function lineOptions<TLine>(
  optionsOf: FillLineOptions<TLine> | undefined,
  line: TLine | undefined,
  row: MultiSubmitRow,
  ctx: MultiSubmitGridContext,
): SelectOption[] | undefined {
  return line && optionsOf ? optionsOf(line, row, ctx) : undefined;
}

/**
 * Clipboard text for one line: labels for select values, the raw text otherwise.
 * @param value - the line value.
 * @param options - the line's options, if it is a select.
 * @returns the text.
 */
function lineToText(value: LineValue, options: SelectOption[] | undefined): string {
  if (Array.isArray(value)) return labelsOf(options ?? [], value);
  return options ? labelOf(options, value) : value;
}

/**
 * One line's value from pasted text, resolving labels against the line's options.
 * @param text - the pasted text for this line.
 * @param isMulti - whether the line field is a multi-select.
 * @param options - the line's options, if it is a select.
 * @returns the line value.
 */
function lineFromText(text: string, isMulti: boolean, options: SelectOption[] | undefined): LineValue {
  if (isMulti) return valuesOf(options ?? [], text);
  return options ? valueOf(options, text) : text.trim();
}

/**
 * A per-line options resolver from a column's option source, or undefined for free-text columns.
 * @param source - the option source.
 * @returns the resolver, or undefined when the source names no options.
 */
function lineOptionsFrom(source: OptionSource): FillLineOptions<unknown> | undefined {
  if (!source.options && !source.optionsKey && !source.ctxOptionsKey) return undefined;
  return (_line, row, ctx) => resolveOptions(source, row, ctx);
}

/**
 * Shared engine for the stacked-line columns: line i of the value goes to line i of the target, and
 * only into lines the target already has. A blank or fully-invalid line leaves the target line as is.
 * A collapsed row shows only its first line, so when a write reaches a later line the row is expanded
 * and the change is in view.
 * @param config - how to read, validate and write one line.
 * @returns the spec.
 */
function stackSpec<TLine extends { id: string }>(config: FillStackConfig<TLine>): FillSpec {
  const { lines, get, optionsOf, normalize = keepText, write, lineEnabled = alwaysEnabled, isEnabled = alwaysEnabled } = config;
  const resolveLine = (incoming: LineValue, line: TLine, row: MultiSubmitRow, ctx: MultiSubmitGridContext): LineValue => {
    const options = optionsOf?.(line, row, ctx);
    if (Array.isArray(get(line))) return asList(incoming).filter((id) => (options ?? []).some((option) => option.value === id));
    if (options) return options.some((option) => option.value === incoming) ? asText(incoming) : '';
    return normalize(asText(incoming));
  };
  return {
    read: (row) => lines(row).map(get),
    apply: (value, row, ctx) => {
      const incoming = asLines(value);
      let wroteHiddenLine = false;
      lines(row).forEach((line, index) => {
        const lineValue = incoming[index];
        if (lineValue === undefined || !lineEnabled(line)) return;
        const next = resolveLine(lineValue, line, row, ctx);
        if (next.length === 0 || isEqual(next, get(line))) return;
        write(ctx, row.id, line, next);
        if (index > 0) wroteHiddenLine = true;
      });
      if (wroteHiddenLine && !row.expanded) ctx.expandRow(row.id);
    },
    toText: (value, row, ctx) =>
      asLines(value)
        .map((lineValue, index) => lineToText(lineValue, lineOptions(optionsOf, lines(row)[index], row, ctx)))
        .join(LINE_SEPARATOR),
    fromText: (text, row, ctx) =>
      text.split(LINE_SEPARATOR.trim()).map((part, index) => {
        const line = lines(row)[index];
        return line ? lineFromText(part, Array.isArray(get(line)), lineOptions(optionsOf, line, row, ctx)) : '';
      }),
    isEnabled,
  };
}

/**
 * Size options for a grading line: the size ranges of the line's selected grading group.
 * @param groups - the product's grading group options.
 * @param gradingGroup - the line's grading group id.
 * @returns the options, value and label both the size name.
 */
export function gradingSizeOptions(groups: GradingGroupOption[], gradingGroup: string): SelectOption[] {
  return (groups.find((group) => group.value === gradingGroup)?.sizeRanges ?? []).map((size) => {
    const name = typeof size === 'string' ? size : size.name;
    return { value: name, label: name };
  });
}

/**
 * A Request Sample grading column. Lines are written by position; a grading-group change clears the
 * line's sizes, as the cell does.
 * @param field - the grading line field.
 * @param source - the column's option source; sizes always come from the line's grading group.
 * @returns the spec.
 */
export function gradingSpec(field: GradingKey, source: OptionSource = {}): FillSpec {
  const sizeOptions: FillLineOptions<GradingGroupLine> = (line, row, ctx) => gradingSizeOptions(ctx.optionsFor(row.id).gradingGroups, line.gradingGroup);
  return stackSpec<GradingGroupLine>({
    lines: (row) => row.gradingLines,
    get: (line) => line[field],
    optionsOf: field === 'size' ? sizeOptions : lineOptionsFrom(source),
    write: (ctx, rowId, line, value) =>
      ctx.updateGradingLine(rowId, line.id, (field === 'gradingGroup' ? { gradingGroup: value, size: [] } : { [field]: value }) as Partial<GradingGroupLine>),
  });
}

/**
 * A Core Product column. Lines are written by position, only once the row has a costing supplier;
 * a length is only written to a line that has sizes, as the cell allows.
 * @param field - the core line field.
 * @param source - the column's option source, for the multi-select fields.
 * @returns the spec.
 */
export function coreSpec(field: CoreKey | CoreMultiKey, source: OptionSource = {}): FillSpec {
  return stackSpec<CoreProductLine>({
    lines: (row) => row.coreLines,
    get: (line) => line[field],
    optionsOf: lineOptionsFrom(source),
    normalize: toNumericText,
    write: (ctx, rowId, line, value) => ctx.updateCoreLine(rowId, line.id, { [field]: value } as Partial<CoreProductLine>),
    lineEnabled: field === 'length' ? (line) => line.size.length > 0 : alwaysEnabled,
    isEnabled: hasCostingSupplier,
  });
}
